'use client';
import React, { useState } from 'react';
import OptionChainHeader from './OptionChainHeader';
import OptionChainTable from './OptionChainTable';
import OptionChainStats from './OptionChainStats';
import OIChartSection from './OIChartSection';
import LivePredictionSection from './LivePredictionSection';
import { useIndex, useTick } from '@/contexts/MarketDataContext';

const FO_TICK_MAP: Record<string, string> = {
  NIFTY: 'NSE_FO|61755',
  BANKNIFTY: 'BANKNIFTY',
  FINNIFTY: 'FINNIFTY',
  SENSEX: 'BSE_FO|SENSEX',
  MIDCPNIFTY: 'MIDCPNIFTY',
  MIDCAP150: 'NIFTYMIDCAP150',
};

type Underlying = 'NIFTY' | 'BANKNIFTY' | 'FINNIFTY' | 'SENSEX' | 'MIDCPNIFTY' | 'MIDCAP150';

interface Props {
  initialUnderlying?: Underlying;
}

// Map dashboard selectedIndex keys → option chain underlying keys
const DASHBOARD_TO_UNDERLYING: Record<string, Underlying> = {
  'NIFTY 50':        'NIFTY',
  'BANKNIFTY':       'BANKNIFTY',
  'FINNIFTY':        'FINNIFTY',
  'SENSEX':          'SENSEX',
  'MIDCPNIFTY':      'MIDCPNIFTY',
  'NIFTYMIDCAP150':  'MIDCAP150',
};

export default function OptionChainContent({ initialUnderlying }: Props) {
  const [underlying, setUnderlying] = useState<Underlying>(initialUnderlying ?? 'NIFTY');
  const [expiry, setExpiry] = useState('29-May-2026');

  const indexMap: Record<string, string> = {
    NIFTY:      'NIFTY 50',
    BANKNIFTY:  'BANKNIFTY',
    FINNIFTY:   'FINNIFTY',
    SENSEX:     'SENSEX',
    MIDCPNIFTY: 'MIDCPNIFTY',
    MIDCAP150:  'NIFTYMIDCAP150',
  };

  const indexData = useIndex(indexMap[underlying]);
  const foTick = useTick(FO_TICK_MAP[underlying] ?? underlying);

  return (
    <div className="space-y-4 lg:space-y-5">
      <OptionChainHeader
        underlying={underlying}
        setUnderlying={setUnderlying}
        expiry={expiry}
        setExpiry={setExpiry}
        spotPrice={indexData?.value}
        spotChange={indexData?.change}
        spotPct={indexData?.pct}
      />

      {/* Three-box row: Options Stats | Option Chain | OI Distribution */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        {/* Box 1 — Options Stats */}
        <div className="xl:col-span-1 min-w-0">
          <OptionChainStats
            underlying={underlying}
            indexData={indexData}
            liveIV={foTick?.iv ?? null}
            liveOI={foTick?.oi ?? null}
          />
        </div>

        {/* Box 2 — Option Chain Table */}
        <div className="xl:col-span-1 min-w-0 overflow-x-auto">
          <OptionChainTable underlying={underlying} spotPrice={indexData?.value} />
        </div>

        {/* Box 3 — Open Interest Distribution */}
        <div className="xl:col-span-1 min-w-0">
          <OIChartSection underlying={underlying} />
        </div>
      </div>

      {/* Live AI Prediction Section */}
      <LivePredictionSection underlying={underlying} spotPrice={indexData?.value} />
    </div>
  );
}